import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';
import CharacterPreview from '../components/CharacterPreview';

const CHARACTER_TYPES = ['boss', 'enemy', 'friendly', 'neutral', 'npc', 'merchant', 'companion'];
const ALIGNMENTS = ['enemy', 'neutral', 'ally'];
const ACTION_CATEGORIES = ['attack', 'movement', 'control', 'utility', 'healing'];
const ACTION_TYPES = ['melee', 'ranged', 'spell', 'ability'];
const DAMAGE_TYPES = ['slashing', 'piercing', 'bludgeoning', 'fire', 'cold', 'lightning', 'thunder', 'poison', 'acid', 'necrotic', 'radiant', 'force', 'psychic'];

export default function CharacterBuilder() {
  const navigate = useNavigate();
  const { characterId } = useParams();
  const isEditing = !!characterId;

  const [character, setCharacter] = useState({
    name: '',
    type: 'npc',
    alignment: 'neutral',
    description: '',
    image: '',
    traits: {},
    stats: {
      hp: { current: 10, max: 10 },
      ac: 10,
      speed: 30,
      strength: 10,
      dexterity: 10,
      constitution: 10,
      intelligence: 10,
      wisdom: 10,
      charisma: 10
    },
    systems: {
      bossBar: { enabled: false, retreatTriggerHP: 0 },
      chaos: { enabled: false, dice: 'd6', table: [] }
    },
    actions: [],
    legendaryActions: [],
    children: []
  });

  const [traitKey, setTraitKey] = useState('');
  const [traitValue, setTraitValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (characterId) {
      loadCharacter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characterId]);

  const loadCharacter = async () => {
    try {
      setIsLoading(true);
      const data = await api.getCharacter(characterId);
      // Ensure arrays and objects have defaults to prevent null errors
      setCharacter({
        ...data,
        traits: data.traits || {},
        stats: {
          hp: { current: 10, max: 10 },
          ac: 10,
          speed: 30,
          strength: 10,
          dexterity: 10,
          constitution: 10,
          intelligence: 10,
          wisdom: 10,
          charisma: 10,
          ...data.stats,
          hp: data.stats?.hp || { current: 10, max: 10 }
        },
        systems: {
          bossBar: { enabled: false, retreatTriggerHP: 0, ...data.systems?.bossBar },
          chaos: { enabled: false, dice: 'd6', table: [], ...data.systems?.chaos, table: data.systems?.chaos?.table || [] }
        },
        actions: data.actions || [],
        legendaryActions: data.legendaryActions || [],
        children: data.children || []
      });
    } catch (error) {
      toast.error('Failed to load character');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!character.name.trim()) {
      toast.error('Character name is required');
      return;
    }

    try {
      setIsSaving(true);
      if (isEditing) {
        await api.updateCharacter(characterId, character);
        toast.success('Character updated');
      } else {
        await api.createCharacter(character);
        toast.success('Character created');
      }
      navigate('/');
    } catch (error) {
      console.error('Save character error:', error);
      toast.error(`Failed to save character: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCharacter({ ...character, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const addTrait = () => {
    if (!traitKey.trim()) return;
    setCharacter({
      ...character,
      traits: { ...character.traits, [traitKey]: traitValue }
    });
    setTraitKey('');
    setTraitValue('');
  };

  const removeTrait = (key) => {
    const newTraits = { ...character.traits };
    delete newTraits[key];
    setCharacter({ ...character, traits: newTraits });
  };

  const addAction = (isLegendary = false) => {
    const newAction = {
      id: `action_${Date.now()}`,
      name: '',
      category: 'attack',
      type: 'melee',
      attackBonus: 0,
      range: '5 ft',
      damage: { dice: '1d6', type: 'slashing' },
      description: ''
    };

    if (isLegendary) {
      setCharacter({ ...character, legendaryActions: [...character.legendaryActions, newAction] });
    } else {
      setCharacter({ ...character, actions: [...character.actions, newAction] });
    }
  };

  const updateAction = (index, field, value, isLegendary = false) => {
    const key = isLegendary ? 'legendaryActions' : 'actions';
    const actions = [...character[key]];
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      actions[index] = {
        ...actions[index],
        [parent]: { ...actions[index][parent], [child]: value }
      };
    } else {
      actions[index] = { ...actions[index], [field]: value };
    }
    
    setCharacter({ ...character, [key]: actions });
  };

  const removeAction = (index, isLegendary = false) => {
    const key = isLegendary ? 'legendaryActions' : 'actions';
    setCharacter({
      ...character,
      [key]: character[key].filter((_, i) => i !== index)
    });
  };

  const addChaosEntry = () => {
    const table = character.systems.chaos.table || [];
    setCharacter({
      ...character,
      systems: {
        ...character.systems,
        chaos: {
          ...character.systems.chaos,
          table: [...table, { roll: table.length + 1, effect: '' }]
        }
      }
    });
  };

  const updateChaosEntry = (index, field, value) => {
    const table = [...character.systems.chaos.table];
    table[index] = { ...table[index], [field]: value };
    setCharacter({
      ...character,
      systems: {
        ...character.systems,
        chaos: { ...character.systems.chaos, table }
      }
    });
  };

  const removeChaosEntry = (index) => {
    setCharacter({
      ...character,
      systems: {
        ...character.systems,
        chaos: {
          ...character.systems.chaos,
          table: character.systems.chaos.table.filter((_, i) => i !== index)
        }
      }
    });
  };

  const addChild = () => {
    const newChild = {
      id: `child_${Date.now()}`,
      name: '',
      type: 'enemy',
      stats: { hp: { current: 10, max: 10 }, ac: 10 },
      actions: []
    };
    setCharacter({ ...character, children: [...character.children, newChild] });
  };

  const updateChild = (index, field, value) => {
    const children = [...character.children];
    if (field.includes('.')) {
      const parts = field.split('.');
      if (parts.length === 3) {
        children[index] = {
          ...children[index],
          [parts[0]]: {
            ...children[index][parts[0]],
            [parts[1]]: {
              ...children[index][parts[0]]?.[parts[1]],
              [parts[2]]: value
            }
          }
        };
      } else {
        children[index] = {
          ...children[index],
          [parts[0]]: { ...children[index][parts[0]], [parts[1]]: value }
        };
      }
    } else {
      children[index] = { ...children[index], [field]: value };
    }
    setCharacter({ ...character, children });
  };

  const removeChild = (index) => {
    setCharacter({
      ...character,
      children: character.children.filter((_, i) => i !== index)
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0c0a09] flex items-center justify-center">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0c0a09]" data-testid="character-builder">
      {/* Header */}
      <header className="border-b border-stone-800 bg-[#0c0a09]/95 backdrop-blur sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="text-stone-400 hover:text-amber-500"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img 
                src="https://customer-assets.emergentagent.com/job_campaign-deck-1/artifacts/zdwsmy37_Copy%20of%20Copy%20of%20Copy%20of%20Copy%20of%20BTT%20TEXT.png" 
                alt="Beyond The Table" 
                className="h-8 w-auto"
              />
              <h1 className="font-cinzel text-xl font-bold text-amber-500 tracking-wider">
                {isEditing ? 'Edit Character' : 'Create Character'}
              </h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-amber-700 hover:bg-amber-600 text-stone-100"
              data-testid="save-character-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Character'}
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <div>
            <ScrollArea className="h-[calc(100vh-150px)]">
              <Tabs defaultValue="basic" className="space-y-6">
                <TabsList className="bg-stone-900 border border-stone-700 p-1 h-auto flex-wrap">
                  <TabsTrigger value="basic" className="font-cinzel text-base px-6 py-3 data-[state=active]:bg-amber-700 data-[state=active]:text-stone-100">Basic Info</TabsTrigger>
                  <TabsTrigger value="attributes" className="font-cinzel text-base px-6 py-3 data-[state=active]:bg-amber-700 data-[state=active]:text-stone-100">Stats & Traits</TabsTrigger>
                  <TabsTrigger value="combat" className="font-cinzel text-base px-6 py-3 data-[state=active]:bg-amber-700 data-[state=active]:text-stone-100">Combat</TabsTrigger>
                </TabsList>

                {/* Basic Tab */}
                <TabsContent value="basic" className="space-y-6 pr-4">
                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader>
                      <CardTitle className="font-cinzel text-amber-500">Basic Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-stone-400">Name</Label>
                          <Input
                            value={character.name}
                            onChange={(e) => setCharacter({ ...character, name: e.target.value })}
                            placeholder="Character name"
                            className="bg-stone-800 border-stone-700 text-stone-200"
                            data-testid="character-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-stone-400">Type</Label>
                          <Select value={character.type} onValueChange={(v) => setCharacter({ ...character, type: v })}>
                            <SelectTrigger className="bg-stone-800 border-stone-700 text-stone-200" data-testid="character-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-stone-800 border-stone-700">
                              {CHARACTER_TYPES.map(type => (
                                <SelectItem key={type} value={type} className="text-stone-200 capitalize">
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-stone-400">Alignment</Label>
                        <Select value={character.alignment || ''} onValueChange={(v) => setCharacter({ ...character, alignment: v })}>
                          <SelectTrigger className="bg-stone-800 border-stone-700 text-stone-200">
                            <SelectValue placeholder="Select alignment" />
                          </SelectTrigger>
                          <SelectContent className="bg-stone-800 border-stone-700">
                            {ALIGNMENTS.map(a => (
                              <SelectItem key={a} value={a} className="text-stone-200 capitalize">
                                {a}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-stone-400">Description</Label>
                        <Textarea
                          value={character.description || ''}
                          onChange={(e) => setCharacter({ ...character, description: e.target.value })}
                          placeholder="Character backstory and description..."
                          className="bg-stone-800 border-stone-700 text-stone-200 min-h-[120px]"
                          data-testid="character-description-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-stone-400">Image</Label>
                        <div className="flex items-center gap-4">
                          {character.image && (
                            <div className="relative w-20 h-20 rounded overflow-hidden border border-stone-700">
                              <img src={character.image} alt="Preview" className="w-full h-full object-cover" />
                              <button
                                onClick={() => setCharacter({ ...character, image: '' })}
                                className="absolute top-1 right-1 p-1 bg-red-900/80 rounded"
                              >
                                <X className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          )}
                          <label className="flex items-center gap-2 px-4 py-2 bg-stone-800 border border-stone-700 rounded cursor-pointer hover:bg-stone-700 transition">
                            <ImageIcon className="w-4 h-4 text-stone-400" />
                            <span className="text-stone-300 text-sm">Upload Image</span>
                            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="image-upload-input" />
                          </label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Stats & Traits Tab (Combined) */}
                <TabsContent value="attributes" className="space-y-6 pr-4">
                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader>
                      <CardTitle className="font-cinzel text-amber-500">Combat Stats</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-stone-400">Current HP</Label>
                          <Input
                            type="number"
                            value={character.stats?.hp?.current || 0}
                            onChange={(e) => setCharacter({
                              ...character,
                              stats: { ...character.stats, hp: { ...character.stats.hp, current: parseInt(e.target.value) || 0 } }
                            })}
                            className="bg-stone-800 border-stone-700 text-stone-200"
                            data-testid="hp-current-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-stone-400">Max HP</Label>
                          <Input
                            type="number"
                            value={character.stats?.hp?.max || 0}
                            onChange={(e) => setCharacter({
                              ...character,
                              stats: { ...character.stats, hp: { ...character.stats.hp, max: parseInt(e.target.value) || 0 } }
                            })}
                            className="bg-stone-800 border-stone-700 text-stone-200"
                            data-testid="hp-max-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-stone-400">Armor Class (AC)</Label>
                          <Input
                            type="number"
                            value={character.stats?.ac || 0}
                            onChange={(e) => setCharacter({
                              ...character,
                              stats: { ...character.stats, ac: parseInt(e.target.value) || 0 }
                            })}
                            className="bg-stone-800 border-stone-700 text-stone-200"
                            data-testid="ac-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-stone-400">Speed (ft)</Label>
                          <Input
                            type="number"
                            value={character.stats?.speed || 0}
                            onChange={(e) => setCharacter({
                              ...character,
                              stats: { ...character.stats, speed: parseInt(e.target.value) || 0 }
                            })}
                            className="bg-stone-800 border-stone-700 text-stone-200"
                            data-testid="speed-input"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader>
                      <CardTitle className="font-cinzel text-amber-500">Ability Scores</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map(stat => (
                          <div key={stat} className="space-y-2">
                            <Label className="text-stone-400 capitalize">{stat.slice(0, 3).toUpperCase()}</Label>
                            <Input
                              type="number"
                              value={character.stats?.[stat] || 10}
                              onChange={(e) => setCharacter({
                                ...character,
                                stats: { ...character.stats, [stat]: parseInt(e.target.value) || 10 }
                              })}
                              className="bg-stone-800 border-stone-700 text-stone-200 text-center"
                            />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Traits */}
                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader>
                      <CardTitle className="font-cinzel text-amber-500">Traits</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-2">
                        <Input
                          value={traitKey}
                          onChange={(e) => setTraitKey(e.target.value)}
                          placeholder="Trait name"
                          className="bg-stone-800 border-stone-700 text-stone-200"
                          data-testid="trait-key-input"
                        />
                        <Input
                          value={traitValue}
                          onChange={(e) => setTraitValue(e.target.value)}
                          placeholder="Value"
                          className="bg-stone-800 border-stone-700 text-stone-200"
                          data-testid="trait-value-input"
                        />
                        <Button onClick={addTrait} variant="outline" className="border-stone-700" data-testid="add-trait-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {Object.entries(character.traits || {}).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between p-2 bg-stone-800 rounded">
                            <span className="text-stone-400">{key}:</span>
                            <span className="text-stone-200">{Array.isArray(value) ? value.join(', ') : String(value)}</span>
                            <Button variant="ghost" size="icon" onClick={() => removeTrait(key)} className="h-6 w-6">
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Combat Tab (Actions, Legendary, Systems, Minions combined) */}
                <TabsContent value="combat" className="space-y-6 pr-4">
                  {/* Actions */}
                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-cinzel text-amber-500">Actions</CardTitle>
                      <Button onClick={() => addAction(false)} variant="outline" size="sm" className="border-stone-700" data-testid="add-action-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Action
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {(character.actions || []).map((action, index) => (
                        <div key={action.id || index} className="p-4 bg-stone-800 rounded border border-stone-700 space-y-3">
                          <div className="flex items-center justify-between">
                            <Input
                              value={action.name}
                              onChange={(e) => updateAction(index, 'name', e.target.value)}
                              placeholder="Action name"
                              className="bg-stone-900 border-stone-600 text-stone-200 flex-1 mr-2"
                            />
                            <Button variant="ghost" size="icon" onClick={() => removeAction(index)} className="text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <Select value={action.category || ''} onValueChange={(v) => updateAction(index, 'category', v)}>
                              <SelectTrigger className="bg-stone-900 border-stone-600 text-stone-200">
                                <SelectValue placeholder="Category" />
                              </SelectTrigger>
                              <SelectContent className="bg-stone-800 border-stone-700">
                                {ACTION_CATEGORIES.map(c => (
                                  <SelectItem key={c} value={c} className="text-stone-200 capitalize">{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={action.type || ''} onValueChange={(v) => updateAction(index, 'type', v)}>
                              <SelectTrigger className="bg-stone-900 border-stone-600 text-stone-200">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent className="bg-stone-800 border-stone-700">
                                {ACTION_TYPES.map(t => (
                                  <SelectItem key={t} value={t} className="text-stone-200 capitalize">{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-stone-500">Attack Bonus</Label>
                              <Input
                                type="number"
                                value={action.attackBonus || 0}
                                onChange={(e) => updateAction(index, 'attackBonus', parseInt(e.target.value) || 0)}
                                className="bg-stone-900 border-stone-600 text-stone-200"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-stone-500">Range</Label>
                              <Input
                                value={action.range || ''}
                                onChange={(e) => updateAction(index, 'range', e.target.value)}
                                placeholder="e.g., 5 ft"
                                className="bg-stone-900 border-stone-600 text-stone-200"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-stone-500">Damage Dice</Label>
                              <Input
                                value={action.damage?.dice || ''}
                                onChange={(e) => updateAction(index, 'damage.dice', e.target.value)}
                                placeholder="e.g., 2d6"
                                className="bg-stone-900 border-stone-600 text-stone-200"
                              />
                            </div>
                          </div>
                          <Select value={action.damage?.type || ''} onValueChange={(v) => updateAction(index, 'damage.type', v)}>
                            <SelectTrigger className="bg-stone-900 border-stone-600 text-stone-200">
                              <SelectValue placeholder="Damage type" />
                            </SelectTrigger>
                            <SelectContent className="bg-stone-800 border-stone-700">
                              {DAMAGE_TYPES.map(d => (
                                <SelectItem key={d} value={d} className="text-stone-200 capitalize">{d}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Textarea
                            value={action.description || ''}
                            onChange={(e) => updateAction(index, 'description', e.target.value)}
                            placeholder="Action description..."
                            className="bg-stone-900 border-stone-600 text-stone-200"
                          />
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Legendary Actions */}
                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-cinzel text-amber-500">Legendary Actions</CardTitle>
                      <Button onClick={() => addAction(true)} variant="outline" size="sm" className="border-stone-700" data-testid="add-legendary-action-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Legendary
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {(character.legendaryActions || []).map((action, index) => (
                        <div key={action.id || index} className="p-4 bg-stone-800 rounded border border-red-900/50 space-y-3">
                          <div className="flex items-center justify-between">
                            <Input
                              value={action.name}
                              onChange={(e) => updateAction(index, 'name', e.target.value, true)}
                              placeholder="Legendary action name"
                              className="bg-stone-900 border-stone-600 text-stone-200 flex-1 mr-2"
                            />
                            <Button variant="ghost" size="icon" onClick={() => removeAction(index, true)} className="text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <Select value={action.category || ''} onValueChange={(v) => updateAction(index, 'category', v, true)}>
                              <SelectTrigger className="bg-stone-900 border-stone-600 text-stone-200">
                                <SelectValue placeholder="Category" />
                              </SelectTrigger>
                              <SelectContent className="bg-stone-800 border-stone-700">
                                {ACTION_CATEGORIES.map(c => (
                                  <SelectItem key={c} value={c} className="text-stone-200 capitalize">{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="space-y-1">
                              <Input
                                type="number"
                                value={action.attackBonus || 0}
                                onChange={(e) => updateAction(index, 'attackBonus', parseInt(e.target.value) || 0, true)}
                                placeholder="Attack bonus"
                                className="bg-stone-900 border-stone-600 text-stone-200"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              value={action.damage?.dice || ''}
                              onChange={(e) => updateAction(index, 'damage.dice', e.target.value, true)}
                              placeholder="Damage dice"
                              className="bg-stone-900 border-stone-600 text-stone-200"
                            />
                            <Select value={action.damage?.type || ''} onValueChange={(v) => updateAction(index, 'damage.type', v, true)}>
                              <SelectTrigger className="bg-stone-900 border-stone-600 text-stone-200">
                                <SelectValue placeholder="Damage type" />
                              </SelectTrigger>
                              <SelectContent className="bg-stone-800 border-stone-700">
                                {DAMAGE_TYPES.map(d => (
                                  <SelectItem key={d} value={d} className="text-stone-200 capitalize">{d}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Textarea
                            value={action.description || ''}
                            onChange={(e) => updateAction(index, 'description', e.target.value, true)}
                            placeholder="Description..."
                            className="bg-stone-900 border-stone-600 text-stone-200"
                          />
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Boss Bar System */}
                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader>
                      <CardTitle className="font-cinzel text-amber-500">Boss Bar System</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-stone-400">Enable Boss Bar</Label>
                        <Switch
                          checked={character.systems?.bossBar?.enabled || false}
                          onCheckedChange={(checked) => setCharacter({
                            ...character,
                            systems: {
                              ...character.systems,
                              bossBar: { ...character.systems?.bossBar, enabled: checked }
                            }
                          })}
                          data-testid="boss-bar-switch"
                        />
                      </div>
                      {character.systems?.bossBar?.enabled && (
                        <div className="space-y-2">
                          <Label className="text-stone-400">Retreat Trigger HP</Label>
                          <Input
                            type="number"
                            value={character.systems?.bossBar?.retreatTriggerHP || 0}
                            onChange={(e) => setCharacter({
                              ...character,
                              systems: {
                                ...character.systems,
                                bossBar: { ...character.systems?.bossBar, retreatTriggerHP: parseInt(e.target.value) || 0 }
                              }
                            })}
                            className="bg-stone-800 border-stone-700 text-stone-200"
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Chaos System */}
                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-cinzel text-amber-500">Chaos System</CardTitle>
                      <Switch
                        checked={character.systems?.chaos?.enabled || false}
                        onCheckedChange={(checked) => setCharacter({
                          ...character,
                          systems: {
                            ...character.systems,
                            chaos: { ...character.systems?.chaos, enabled: checked }
                          }
                        })}
                        data-testid="chaos-system-switch"
                      />
                    </CardHeader>
                    {character.systems?.chaos?.enabled && (
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-stone-400">Chaos Dice</Label>
                          <Input
                            value={character.systems?.chaos?.dice || 'd6'}
                            onChange={(e) => setCharacter({
                              ...character,
                              systems: {
                                ...character.systems,
                                chaos: { ...character.systems?.chaos, dice: e.target.value }
                              }
                            })}
                            placeholder="e.g., d6"
                            className="bg-stone-800 border-stone-700 text-stone-200"
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-stone-400">Chaos Table</Label>
                            <Button onClick={addChaosEntry} variant="outline" size="sm" className="border-stone-700">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          {(character.systems?.chaos?.table || []).map((entry, index) => (
                            <div key={index} className="flex gap-2 items-center">
                              <Input
                                type="number"
                                value={entry.roll}
                                onChange={(e) => updateChaosEntry(index, 'roll', parseInt(e.target.value) || 0)}
                                className="w-16 bg-stone-800 border-stone-700 text-stone-200"
                              />
                              <Input
                                value={entry.effect}
                                onChange={(e) => updateChaosEntry(index, 'effect', e.target.value)}
                                placeholder="Effect description"
                                className="flex-1 bg-stone-800 border-stone-700 text-stone-200"
                              />
                              <Button variant="ghost" size="icon" onClick={() => removeChaosEntry(index)} className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Minions */}
                  <Card className="bg-stone-900 border-stone-700">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-cinzel text-amber-500">Minions / Children</CardTitle>
                      <Button onClick={addChild} variant="outline" size="sm" className="border-stone-700" data-testid="add-minion-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Minion
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {(character.children || []).map((child, index) => (
                        <div key={child.id || index} className="p-4 bg-stone-800 rounded border border-stone-700 space-y-3">
                          <div className="flex items-center justify-between">
                            <Input
                              value={child.name}
                              onChange={(e) => updateChild(index, 'name', e.target.value)}
                              placeholder="Minion name"
                              className="bg-stone-900 border-stone-600 text-stone-200 flex-1 mr-2"
                            />
                            <Button variant="ghost" size="icon" onClick={() => removeChild(index)} className="text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <Select value={child.type || 'enemy'} onValueChange={(v) => updateChild(index, 'type', v)}>
                              <SelectTrigger className="bg-stone-900 border-stone-600 text-stone-200">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-stone-800 border-stone-700">
                                {CHARACTER_TYPES.map(t => (
                                  <SelectItem key={t} value={t} className="text-stone-200 capitalize">{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              value={child.stats?.ac || 10}
                              onChange={(e) => updateChild(index, 'stats.ac', parseInt(e.target.value) || 10)}
                              placeholder="AC"
                              className="bg-stone-900 border-stone-600 text-stone-200"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              type="number"
                              value={child.stats?.hp?.current || 0}
                              onChange={(e) => updateChild(index, 'stats.hp.current', parseInt(e.target.value) || 0)}
                              placeholder="Current HP"
                              className="bg-stone-900 border-stone-600 text-stone-200"
                            />
                            <Input
                              type="number"
                              value={child.stats?.hp?.max || 0}
                              onChange={(e) => updateChild(index, 'stats.hp.max', parseInt(e.target.value) || 0)}
                              placeholder="Max HP"
                              className="bg-stone-900 border-stone-600 text-stone-200"
                            />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </ScrollArea>
          </div>

          {/* Preview Section */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              <h2 className="font-cinzel text-sm font-semibold text-stone-400 uppercase tracking-wider mb-4">
                Live Preview
              </h2>
              <CharacterPreview character={character} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
